import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  Grid, IconButton, InputAdornment, MenuItem, Skeleton, Stack, TextField,
  Tooltip, Typography
} from '@mui/material';
import Swal from 'sweetalert2';
import Icon from '../../../components/icon';
import MsiDatagrid from '../../../components/custom/MsiDatagridAuto';
import { useToast } from '../../../components/ToastProvider';
import api from '../../../utils/axiosConfig';
import { buildApplyPayload, isApplyActionDisabled, isConfirmationApplyDisabled, readPlanImpact, requiresPlanConfirmation } from './plan-impact';
import './request-change-work-order.scss';

type RequestStatus = '' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'APPLIED' | 'CANCELLED';
type ReviewAction = 'approve' | 'reject';

interface SelectOption {
  id: number | string;
  nama?: string;
  name?: string;
  kode?: string;
  label?: string;
  [key: string]: any;
}

interface ModuleOptions {
  kedinding_site_id: number;
  sites: SelectOption[];
  designers: SelectOption[];
  proses_reguler: SelectOption[];
  proses_borongan: SelectOption[];
}

interface ChangeFormState {
  wo_order_id: number | string | '';
  site_id_baru: number | string | '';
  kode_wo_baru: string;
  designer_id_baru: number | string | '';
  wo_proses_id_baru: number | string | '';
  wo_proses_borongan_id_baru: number | string | '';
  alasan: string;
}

const emptyForm: ChangeFormState = {
  wo_order_id: '', site_id_baru: '', kode_wo_baru: '', designer_id_baru: '',
  wo_proses_id_baru: '', wo_proses_borongan_id_baru: '', alasan: '',
};

const statuses: { value: RequestStatus; label: string }[] = [
  { value: '', label: 'Semua status' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'APPLIED', label: 'Applied' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const statusColor = (status?: string): 'warning' | 'info' | 'error' | 'success' | 'default' => {
  switch (String(status || '').toUpperCase()) {
    case 'PENDING': return 'warning';
    case 'APPROVED': return 'info';
    case 'REJECTED': return 'error';
    case 'APPLIED': return 'success';
    default: return 'default';
  }
};

const errorMessage = (error: any) => error?.response?.data?.message || error?.message || 'Terjadi kesalahan. Silakan coba lagi.';
const arrayData = (payload: any) => Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
const entityId = (value: any) => value?.id ?? value?.value ?? value;
const optionLabel = (value?: SelectOption | null) => value?.label || value?.nama || value?.name || value?.kode || String(value?.id || '');
const actorLabel = (value: any) => value?.nama || value?.name || value?.username || value?.kode || (typeof value === 'string' || typeof value === 'number' ? String(value) : '-');
const formatDateTime = (value?: string | null) => value ? new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '-';
const requestId = (row: any) => row?.id ?? row?.request_change_work_order_id ?? row?.request_id;
const requestNumber = (row: any) => row?.nomor_request ?? row?.no_request ?? row?.request_number ?? `#${requestId(row)}`;
const requestSource = (detail: any) => detail?.request ?? detail?.data ?? detail ?? {};
const currentWorkOrder = (source: any) => source?.work_order ?? source?.wo_order ?? source?.workOrder ?? {};

const comparisonField = (comparison: any, ...keys: string[]) => {
  for (const key of keys) if (comparison?.[key]) return comparison[key];
  return {};
};

const comparisonSide = (field: any, side: 'old' | 'new') => {
  if (side === 'old') return field?.old ?? field?.lama ?? field?.old_value ?? field?.value_old ?? field?.sebelum;
  return field?.new ?? field?.baru ?? field?.new_value ?? field?.value_new ?? field?.sesudah;
};

const valueId = (value: any) => entityId(value) ?? '';
const processLabel = (value: any) => {
  if (value == null || value === '') return '-';
  if (typeof value !== 'object') return String(value);
  const process = value.process || value.proses || value.data || value;
  const type = String(value.type || value.tipe || value.process_type || value.jenis || process.type || process.tipe || '').toUpperCase();
  const name = process.nama || process.name || process.label || process.kode || '-';
  return type ? `${type} — ${name}` : name;
};
const displayValue = (value: any, process = false) => {
  if (process) return processLabel(value);
  if (value == null || value === '') return '-';
  if (typeof value === 'object') return value.nama || value.name || value.label || value.kode || value.value || '-';
  return String(value);
};

const isAllowed = (allowed: any, action: string) => {
  if (Array.isArray(allowed)) return allowed.some(item => typeof item === 'string' ? item.toLowerCase() === action : String(item?.key || item?.action || '').toLowerCase() === action);
  return Boolean(allowed?.[action] ?? allowed?.[`can_${action}`]);
};

const changedFieldLabel = (field: string) => {
  const normalized = String(field).toLowerCase();
  if (normalized.includes('site')) return 'Site';
  if (normalized.includes('kode')) return 'Kode WO';
  if (normalized.includes('designer')) return 'Designer';
  if (normalized.includes('proses') || normalized.includes('process')) return 'Proses';
  return field;
};

export default function RequestChangeWorkOrderIndex() {
  const { showToast } = useToast();
  const listController = useRef<AbortController | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [status, setStatus] = useState<RequestStatus>('');
  const [perPage, setPerPage] = useState(50);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [conflictMessage, setConflictMessage] = useState('');
  const [selectedId, setSelectedId] = useState<number | string | null>(null);
  const [detail, setDetail] = useState<any>(null);

  const [options, setOptions] = useState<ModuleOptions>({ kedinding_site_id: 2, sites: [], designers: [], proses_reguler: [], proses_borongan: [] });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [form, setForm] = useState<ChangeFormState>(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [workOrderSearch, setWorkOrderSearch] = useState('');
  const [workOrdersLoading, setWorkOrdersLoading] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<any>(null);

  const [reviewAction, setReviewAction] = useState<ReviewAction | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [regenerationConfirmed, setRegenerationConfirmed] = useState(false);
  const [confirmationImpact, setConfirmationImpact] = useState<any>(null);

  const loadList = useCallback(async () => {
    listController.current?.abort();
    const controller = new AbortController();
    listController.current = controller;
    setLoading(true); setListError('');
    try {
      const { data } = await api.get('/production/request-change-work-order/list', {
        params: { status: status || undefined, search: appliedSearch || undefined, per_page: perPage },
        signal: controller.signal,
      });
      if (data?.success === false) throw new Error(data.message || 'Daftar request gagal dimuat.');
      const result = data?.data;
      if (!controller.signal.aborted) setRows(Array.isArray(result) ? result : arrayData(result));
    } catch (requestError: any) {
      if (requestError?.code !== 'ERR_CANCELED' && !controller.signal.aborted) setListError(errorMessage(requestError));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [appliedSearch, perPage, status]);

  useEffect(() => {
    loadList();
    return () => listController.current?.abort();
  }, [loadList]);

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const { data } = await api.get('/production/request-change-work-order/options');
      if (data?.success === false) throw new Error(data.message || 'Pilihan form gagal dimuat.');
      const result = data?.data || {};
      setOptions({
        kedinding_site_id: Number(result.kedinding_site_id || 2),
        sites: arrayData(result.sites), designers: arrayData(result.designers),
        proses_reguler: arrayData(result.proses_reguler), proses_borongan: arrayData(result.proses_borongan),
      });
    } catch (requestError) {
      setFormError(errorMessage(requestError));
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id: number | string, keepOpen = true) => {
    if (keepOpen) setDetailOpen(true);
    setSelectedId(id); setDetailLoading(true); setDetailError(''); setConflictMessage('');
    try {
      const { data } = await api.get(`/production/request-change-work-order/detail/${id}`);
      if (data?.success === false) throw new Error(data.message || 'Detail request gagal dimuat.');
      setDetail(data?.data || null);
    } catch (requestError) {
      setDetailError(errorMessage(requestError)); setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!formOpen || formMode !== 'add') return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setWorkOrdersLoading(true);
      try {
        const { data } = await api.get('/production/request-change-work-order/work-orders', { params: { search: workOrderSearch || undefined, limit: 30 }, signal: controller.signal });
        if (data?.success === false) throw new Error(data.message || 'Work Order gagal dicari.');
        setWorkOrders(arrayData(data?.data));
      } catch (requestError: any) {
        if (requestError?.code !== 'ERR_CANCELED') setFormError(errorMessage(requestError));
      } finally {
        if (!controller.signal.aborted) setWorkOrdersLoading(false);
      }
    }, 350);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [formMode, formOpen, workOrderSearch]);

  const fillFromWorkOrder = (wo: any) => {
    const siteId = entityId(wo?.site_id ?? wo?.site);
    setSelectedWorkOrder(wo);
    setWorkOrderSearch(`${wo?.kode_wo || wo?.kode || wo?.id} — ${wo?.nama_produk || wo?.product?.nama || wo?.product || 'Work Order'}`);
    setForm(current => ({
      ...current,
      wo_order_id: entityId(wo?.wo_order_id ?? wo?.id) || '',
      site_id_baru: siteId || '',
      kode_wo_baru: wo?.kode_wo ?? wo?.kode ?? '',
      designer_id_baru: entityId(wo?.designer_id ?? wo?.designer) || '',
      wo_proses_id_baru: Number(siteId) === options.kedinding_site_id ? '' : entityId(wo?.wo_proses_id ?? wo?.proses_reguler ?? wo?.proses) || '',
      wo_proses_borongan_id_baru: Number(siteId) === options.kedinding_site_id ? entityId(wo?.wo_proses_borongan_id ?? wo?.proses_borongan ?? wo?.proses) || '' : '',
    }));
    setFormErrors({}); setFormError('');
  };

  const openAdd = () => {
    setFormMode('add'); setForm(emptyForm); setFormErrors({}); setFormError('');
    setSelectedWorkOrder(null); setWorkOrderSearch(''); setWorkOrders([]); setFormOpen(true);
    loadOptions();
  };

  const openEdit = () => {
    if (!detail) return;
    const source = requestSource(detail);
    const wo = currentWorkOrder(source);
    const comparison = detail?.comparison || source?.comparison || {};
    const siteField = comparisonField(comparison, 'site');
    const codeField = comparisonField(comparison, 'kode_wo', 'kode');
    const designerField = comparisonField(comparison, 'designer');
    const processField = comparisonField(comparison, 'proses', 'process');
    const newProcess = comparisonSide(processField, 'new') || {};
    const siteId = source.site_id_baru ?? valueId(comparisonSide(siteField, 'new'));
    const processType = String(newProcess?.type || newProcess?.tipe || source.process_type_baru || '').toUpperCase();
    setFormMode('edit'); setSelectedWorkOrder(wo);
    setForm({
      wo_order_id: source.wo_order_id ?? entityId(wo) ?? '',
      site_id_baru: siteId || '',
      kode_wo_baru: source.kode_wo_baru ?? displayValue(comparisonSide(codeField, 'new')) ?? '',
      designer_id_baru: source.designer_id_baru ?? valueId(comparisonSide(designerField, 'new')) ?? '',
      wo_proses_id_baru: source.wo_proses_id_baru ?? (processType === 'REGULER' ? valueId(newProcess) : ''),
      wo_proses_borongan_id_baru: source.wo_proses_borongan_id_baru ?? (processType === 'BORONGAN' ? valueId(newProcess) : ''),
      alasan: source.alasan ?? detail?.alasan ?? '',
    });
    setFormErrors({}); setFormError(''); setFormOpen(true); loadOptions();
  };

  const handleSiteChange = (value: string) => {
    const siteId = value === '' ? '' : Number(value);
    setForm(current => ({
      ...current, site_id_baru: siteId,
      wo_proses_id_baru: Number(siteId) === options.kedinding_site_id ? '' : current.wo_proses_id_baru,
      wo_proses_borongan_id_baru: Number(siteId) === options.kedinding_site_id ? current.wo_proses_borongan_id_baru : '',
    }));
    setFormErrors(current => ({ ...current, site_id_baru: '', wo_proses_id_baru: '', wo_proses_borongan_id_baru: '' }));
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!form.wo_order_id) errors.wo_order_id = 'Work Order wajib dipilih.';
    if (!form.site_id_baru) errors.site_id_baru = 'Site wajib dipilih.';
    if (!form.kode_wo_baru.trim()) errors.kode_wo_baru = 'Kode WO wajib diisi.';
    if (!form.alasan.trim()) errors.alasan = 'Alasan perubahan wajib diisi.';
    if (Number(form.site_id_baru) === options.kedinding_site_id && !form.wo_proses_borongan_id_baru) errors.wo_proses_borongan_id_baru = 'Proses borongan wajib dipilih untuk Kedinding.';
    if (form.site_id_baru && Number(form.site_id_baru) !== options.kedinding_site_id && !form.wo_proses_id_baru) errors.wo_proses_id_baru = 'Proses reguler wajib dipilih.';
    setFormErrors(errors);
    return !Object.keys(errors).length;
  };

  const submitForm = async (event: FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;
    setFormSubmitting(true); setFormError('');
    const isKedinding = Number(form.site_id_baru) === options.kedinding_site_id;
    const payload = {
      ...(formMode === 'add' ? { wo_order_id: form.wo_order_id } : {}),
      site_id_baru: form.site_id_baru,
      kode_wo_baru: form.kode_wo_baru.trim(),
      designer_id_baru: form.designer_id_baru || null,
      wo_proses_id_baru: isKedinding ? null : form.wo_proses_id_baru,
      wo_proses_borongan_id_baru: isKedinding ? form.wo_proses_borongan_id_baru : null,
      alasan: form.alasan.trim(),
    };
    try {
      const response = formMode === 'add'
        ? await api.post('/production/request-change-work-order/add', payload)
        : await api.put(`/production/request-change-work-order/edit/${selectedId}`, payload);
      const result = response.data;
      if (result?.success === false) {
        if (result?.errors) setFormErrors(Object.fromEntries(Object.entries(result.errors).map(([key, value]: any) => [key, Array.isArray(value) ? value[0] : String(value)])));
        throw new Error(result.message || 'Request gagal disimpan.');
      }
      const newId = requestId(result?.data) ?? (typeof result?.data === 'number' || typeof result?.data === 'string' ? result.data : selectedId);
      showToast(result?.message || `Request berhasil ${formMode === 'add' ? 'dibuat' : 'diperbarui'}.`, 'success');
      setFormOpen(false); await loadList();
      if (newId) await loadDetail(newId);
    } catch (requestError: any) {
      const validation = requestError?.response?.data?.errors;
      if (validation) setFormErrors(Object.fromEntries(Object.entries(validation).map(([key, value]: any) => [key, Array.isArray(value) ? value[0] : String(value)])));
      setFormError(errorMessage(requestError));
    } finally {
      setFormSubmitting(false);
    }
  };

  const refreshAfterMutation = async (message: string) => {
    showToast(message, 'success');
    await loadList();
    if (selectedId) await loadDetail(selectedId, false);
  };

  const submitReview = async (event: FormEvent) => {
    event.preventDefault();
    if (!reviewAction || !selectedId || (reviewAction === 'reject' && !reviewNotes.trim())) return;
    setActionLoading(reviewAction);
    try {
      const { data } = await api.post(`/production/request-change-work-order/${reviewAction}/${selectedId}`, { review_notes: reviewNotes.trim() || null });
      if (data?.success === false) throw new Error(data.message || 'Review request gagal diproses.');
      setReviewAction(null); setReviewNotes('');
      await refreshAfterMutation(data?.message || (reviewAction === 'approve' ? 'Request berhasil disetujui. Work Order belum berubah.' : 'Request berhasil ditolak.'));
    } catch (requestError) {
      const message = errorMessage(requestError); showToast(message, 'error');
      if (selectedId) await loadDetail(selectedId, false);
      setConflictMessage(message);
    } finally {
      setActionLoading('');
    }
  };

  const cancelRequest = async () => {
    if (!selectedId) return;
    const confirmation = await Swal.fire({
      title: 'Batalkan request?', text: 'Request yang dibatalkan tidak dapat diproses kembali.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Ya, batalkan', cancelButtonText: 'Kembali', reverseButtons: true,
    });
    if (!confirmation.isConfirmed) return;
    setActionLoading('cancel');
    try {
      const { data } = await api.post(`/production/request-change-work-order/cancel/${selectedId}`);
      if (data?.success === false) throw new Error(data.message || 'Request gagal dibatalkan.');
      await refreshAfterMutation(data?.message || 'Request berhasil dibatalkan.');
    } catch (requestError) {
      const message = errorMessage(requestError); showToast(message, 'error');
      await loadDetail(selectedId, false);
      setConflictMessage(message);
    } finally {
      setActionLoading('');
    }
  };

  const handleApplyConflict = async (requestError: any) => {
    const response = requestError?.response;
    if (response?.status !== 409) throw requestError;
    const result = response?.data || {};
    const message = result.message || 'Work Order berubah atau kondisi plan tidak lagi sesuai.';
    if (result?.data?.confirmation_required === true) {
      setConfirmationImpact(result.data.plan_impact || detail?.plan_impact);
      setRegenerationConfirmed(false); setConfirmOpen(true);
    } else {
      showToast(message, 'error');
    }
    if (selectedId) await loadDetail(selectedId, false);
    setConflictMessage(message);
  };

  const applyChange = async (confirmRegeneratePlan: boolean) => {
    if (!selectedId) return;
    setActionLoading('apply'); setConflictMessage('');
    try {
      const { data } = await api.post(`/production/request-change-work-order/apply/${selectedId}`, buildApplyPayload(confirmRegeneratePlan));
      if (data?.success === false) throw new Error(data.message || 'Perubahan gagal diterapkan.');
      setConfirmOpen(false); setRegenerationConfirmed(false);
      await refreshAfterMutation(data?.message || 'Perubahan berhasil diterapkan ke Work Order.');
    } catch (requestError: any) {
      try { await handleApplyConflict(requestError); }
      catch (unexpectedError) { const message = errorMessage(unexpectedError); showToast(message, 'error'); if (selectedId) await loadDetail(selectedId, false); setConflictMessage(message); }
    } finally {
      setActionLoading('');
    }
  };

  const beginApply = async () => {
    const impact = detail?.plan_impact || {};
    if (requiresPlanConfirmation(impact)) {
      setConfirmationImpact(impact); setRegenerationConfirmed(false); setConfirmOpen(true);
      return;
    }
    const confirmation = await Swal.fire({
      title: 'Terapkan perubahan?', text: 'Work Order akan diperbarui sesuai request yang telah disetujui.', icon: 'question',
      showCancelButton: true, confirmButtonText: 'Terapkan perubahan', cancelButtonText: 'Batal', reverseButtons: true,
    });
    if (confirmation.isConfirmed) await applyChange(false);
  };

  const tableRows = useMemo(() => rows.map(row => {
    const oldCode = row.kode_wo_lama ?? row.kode_lama ?? row.work_order?.kode ?? row.wo_order?.kode ?? '-';
    const newCode = row.kode_wo_baru ?? row.kode_baru ?? oldCode;
    return {
      id: requestId(row), nomor_request: requestNumber(row), work_order: `${oldCode} → ${newCode}`,
      perubahan: row.changed_fields || [], status: row.status || '-',
      tanggal_request: row.requested_at ?? row.created_at ?? row.tanggal_request,
      requester: actorLabel(row.requester ?? row.requested_by ?? row.created_by_user ?? row.created_by),
      _source: row,
    };
  }), [rows]);

  const customRender = [
    { key: 'nomor_request', renderCell: ({ value }: any) => <Typography fontWeight={800} color="primary.main">{value}</Typography> },
    { key: 'work_order', renderCell: ({ value }: any) => <Typography variant="body2" fontWeight={700}>{value}</Typography> },
    { key: 'perubahan', renderCell: ({ value }: any) => <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>{arrayData(value).length ? arrayData(value).map((field: any) => <Chip key={String(field?.key || field)} size="small" variant="outlined" label={changedFieldLabel(field?.label || field?.key || field)} />) : <Typography variant="caption" color="text.secondary">Tidak ada</Typography>}</Stack> },
    { key: 'status', renderCell: ({ value }: any) => <Chip size="small" color={statusColor(value)} label={String(value || '-').toUpperCase()} /> },
    { key: 'tanggal_request', renderCell: ({ value }: any) => <Typography variant="body2">{formatDateTime(value)}</Typography> },
  ];
  const actions = [{
    key: 'aksi', field: 'aksi', headerName: 'Aksi', align: 'center', minWidth: 90,
    renderCell: ({ row }: any) => <Tooltip title="Lihat detail"><IconButton size="small" color="primary" onClick={() => loadDetail(row.id)}><Icon icon="mdi:arrow-right" /></IconButton></Tooltip>,
  }];

  const source = requestSource(detail);
  const comparison = detail?.comparison || source?.comparison || {};
  const allowedActions = detail?.allowed_actions || source?.allowed_actions || {};
  const isStale = Boolean(detail?.is_stale ?? source?.is_stale);
  const planImpact = detail?.plan_impact || source?.plan_impact || {};
  const hasPlanImpact = Boolean(
    planImpact.requires_confirmation || planImpact.preserved_plan_count || planImpact.removed_plan_count ||
    planImpact.new_plan_count || planImpact.final_plan_count || arrayData(planImpact.final_steps).length
  );
  const comparisonRows = [
    { key: 'site', label: 'Site', data: comparisonField(comparison, 'site') },
    { key: 'kode', label: 'Kode WO', data: comparisonField(comparison, 'kode_wo', 'kode') },
    { key: 'designer', label: 'Designer', data: comparisonField(comparison, 'designer') },
    { key: 'process', label: 'Proses', data: comparisonField(comparison, 'proses', 'process'), process: true },
  ];

  return <div className="request-change-wo-page">
    <Box className="request-change-heading">
      <Box><Typography variant="h4" fontWeight={850}>Request Change Work Order</Typography><Typography color="text.secondary">Ajukan, review, dan terapkan perubahan Work Order dengan kontrol regenerasi plan.</Typography></Box>
      <Stack direction="row" spacing={1}><Tooltip title="Muat ulang"><span><IconButton className="refresh-button" onClick={loadList} disabled={loading}><Icon icon="mdi:refresh" /></IconButton></span></Tooltip><Button size="large" variant="contained" onClick={openAdd} startIcon={<Icon icon="mdi:plus" />}>Buat Request</Button></Stack>
    </Box>

    <Card className="request-change-shell">
      <Box component="form" className="request-filter" onSubmit={event => { event.preventDefault(); setAppliedSearch(search.trim()); }}>
        <TextField size="small" className="request-search" label="Cari request atau kode WO" value={search} onChange={event => setSearch(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Icon icon="mdi:magnify" /></InputAdornment> }} />
        <TextField select size="small" label="Status" value={status} onChange={event => setStatus(event.target.value as RequestStatus)}>{statuses.map(item => <MenuItem key={item.value || 'all'} value={item.value}>{item.label}</MenuItem>)}</TextField>
        <TextField select size="small" label="Data per halaman" value={perPage} onChange={event => setPerPage(Number(event.target.value))}>{[10, 25, 50].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}</TextField>
        <Button type="submit" variant="contained" disabled={loading}>Tampilkan</Button>
        {!loading && !listError && <Chip label={`${rows.length} request`} />}
      </Box>
      <Divider />
      <Box className="request-grid">
        {loading ? <ListLoading /> : listError ? <StatePanel icon="mdi:cloud-alert-outline" title="Daftar gagal dimuat" text={listError} action={<Button variant="outlined" onClick={loadList} startIcon={<Icon icon="mdi:refresh" />}>Coba lagi</Button>} /> : !tableRows.length ? <StatePanel icon="mdi:file-document-alert-outline" title="Belum ada request" text="Tidak ada request perubahan Work Order pada filter ini." /> : <MsiDatagrid array={tableRows} actions={actions} customRender={customRender} hiddenColumns={['id', '_source']} groupPanel={false} />}
      </Box>
    </Card>

    <Dialog open={formOpen} onClose={() => !formSubmitting && setFormOpen(false)} fullWidth maxWidth="md" PaperProps={{ className: 'change-form-dialog' }}>
      <form onSubmit={submitForm}>
        <DialogTitle><Typography variant="h6" fontWeight={850}>{formMode === 'add' ? 'Buat Request Perubahan' : 'Edit Request Perubahan'}</Typography><Typography variant="body2" color="text.secondary">Isi kondisi akhir Work Order yang diinginkan.</Typography></DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>{formError}</Alert>}
          {optionsLoading && <Alert severity="info" icon={<CircularProgress size={18} />}>Memuat pilihan form...</Alert>}
          <Grid container spacing={2.2} mt={0.1}>
            <Grid item xs={12}>
              {formMode === 'add' ? <Autocomplete
                options={workOrders} value={selectedWorkOrder} loading={workOrdersLoading} filterOptions={items => items}
                inputValue={workOrderSearch} onInputChange={(_event, value, reason) => { if (reason !== 'reset') setWorkOrderSearch(value); }}
                onChange={(_event, value) => value ? fillFromWorkOrder(value) : (setSelectedWorkOrder(null), setForm(emptyForm))}
                getOptionLabel={wo => `${wo.kode_wo || wo.kode || wo.id} — ${wo.nama_produk || wo.product?.nama || wo.product || 'Work Order'}`}
                isOptionEqualToValue={(option, value) => String(entityId(option)) === String(entityId(value))}
                renderInput={params => <TextField {...params} required label="Work Order" error={Boolean(formErrors.wo_order_id)} helperText={formErrors.wo_order_id || 'Ketik kode WO untuk pencarian remote.'} InputProps={{ ...params.InputProps, endAdornment: <>{workOrdersLoading && <CircularProgress size={18} />}{params.InputProps.endAdornment}</> }} />}
              /> : <TextField fullWidth disabled label="Work Order" value={`${source?.kode_work_order || source?.kode_wo_lama || currentWorkOrder(source)?.kode || form.wo_order_id}`} helperText="Work Order tidak dapat diganti saat edit request." />}
            </Grid>
            <Grid item xs={12} md={6}><TextField select required fullWidth label="Site tujuan" value={form.site_id_baru} onChange={event => handleSiteChange(event.target.value)} error={Boolean(formErrors.site_id_baru)} helperText={formErrors.site_id_baru}>{options.sites.map(item => <MenuItem key={String(item.id)} value={item.id}>{optionLabel(item)}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={6}><TextField required fullWidth label="Kode WO baru" value={form.kode_wo_baru} onChange={event => setForm(current => ({ ...current, kode_wo_baru: event.target.value }))} error={Boolean(formErrors.kode_wo_baru)} helperText={formErrors.kode_wo_baru} /></Grid>
            <Grid item xs={12} md={6}><TextField select fullWidth label="Designer" value={form.designer_id_baru} onChange={event => setForm(current => ({ ...current, designer_id_baru: event.target.value }))} helperText="Designer boleh dikosongkan."><MenuItem value=""><em>Tanpa designer</em></MenuItem>{options.designers.map(item => <MenuItem key={String(item.id)} value={item.id}>{optionLabel(item)}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={6}>
              {Number(form.site_id_baru) === options.kedinding_site_id ? <TextField select required fullWidth label="Proses borongan" value={form.wo_proses_borongan_id_baru} onChange={event => setForm(current => ({ ...current, wo_proses_borongan_id_baru: event.target.value, wo_proses_id_baru: '' }))} error={Boolean(formErrors.wo_proses_borongan_id_baru)} helperText={formErrors.wo_proses_borongan_id_baru || 'Wajib untuk Site Kedinding.'}>{options.proses_borongan.map(item => <MenuItem key={String(item.id)} value={item.id}>{optionLabel(item)}</MenuItem>)}</TextField> : <TextField select required fullWidth disabled={!form.site_id_baru} label="Proses reguler" value={form.wo_proses_id_baru} onChange={event => setForm(current => ({ ...current, wo_proses_id_baru: event.target.value, wo_proses_borongan_id_baru: '' }))} error={Boolean(formErrors.wo_proses_id_baru)} helperText={formErrors.wo_proses_id_baru || 'Digunakan untuk site selain Kedinding.'}>{options.proses_reguler.map(item => <MenuItem key={String(item.id)} value={item.id}>{optionLabel(item)}</MenuItem>)}</TextField>}
            </Grid>
            <Grid item xs={12}><TextField required fullWidth multiline minRows={3} label="Alasan perubahan" value={form.alasan} onChange={event => setForm(current => ({ ...current, alasan: event.target.value }))} error={Boolean(formErrors.alasan)} helperText={formErrors.alasan} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setFormOpen(false)} disabled={formSubmitting}>Batal</Button><Button type="submit" variant="contained" disabled={formSubmitting || optionsLoading} startIcon={formSubmitting ? <CircularProgress size={18} color="inherit" /> : <Icon icon="mdi:content-save-outline" />}>{formSubmitting ? 'Menyimpan...' : formMode === 'add' ? 'Buat request' : 'Simpan perubahan'}</Button></DialogActions>
      </form>
    </Dialog>

    <Dialog open={detailOpen} onClose={() => !actionLoading && setDetailOpen(false)} fullWidth maxWidth="lg" PaperProps={{ className: 'change-detail-dialog' }}>
      <DialogTitle className="detail-title"><Box><Typography variant="h5" fontWeight={850}>Detail {detail ? requestNumber(source) : 'Request'}</Typography><Typography variant="body2" color="text.secondary">Approve hanya menyetujui request. Work Order baru berubah setelah Apply.</Typography></Box>{detail && <Chip color={statusColor(source?.status)} label={String(source?.status || '-').toUpperCase()} />}</DialogTitle>
      <DialogContent dividers className="detail-content">
        {detailLoading ? <DetailLoading /> : detailError ? <StatePanel icon="mdi:cloud-alert-outline" title="Detail gagal dimuat" text={detailError} action={selectedId ? <Button variant="outlined" onClick={() => loadDetail(selectedId, false)}>Coba lagi</Button> : undefined} /> : detail && <Stack spacing={2.2}>
          {conflictMessage && <Alert severity="error" onClose={() => setConflictMessage('')}>{conflictMessage}</Alert>}
          {isStale && <Alert severity="warning" icon={<Icon icon="mdi:alert-octagon-outline" />}><strong>Request sudah tidak sinkron.</strong> Work Order telah berubah di luar request ini. Edit ulang request sebelum melakukan approve atau apply.</Alert>}

          <Card variant="outlined" className="comparison-card">
            <Box className="section-heading"><Box><Typography variant="h6" fontWeight={800}>Perbandingan Perubahan</Typography><Typography variant="body2" color="text.secondary">Nilai lama dan kondisi akhir yang diminta.</Typography></Box><Icon icon="mdi:compare-horizontal" /></Box>
            <Box className="comparison-header"><Typography>Field</Typography><Typography>Data Lama</Typography><Typography>Data Baru</Typography></Box>
            {comparisonRows.map(row => <Box key={row.key} className={`comparison-row ${row.data?.changed ? 'changed' : ''}`}><Typography fontWeight={750}>{row.label}{row.data?.changed && <Chip size="small" color="primary" label="Berubah" />}</Typography><Typography>{displayValue(comparisonSide(row.data, 'old'), row.process)}</Typography><Typography>{displayValue(comparisonSide(row.data, 'new'), row.process)}</Typography></Box>)}
          </Card>

          <Grid container spacing={2}>
            <Grid item xs={12} md={7}><Card variant="outlined" className="request-notes"><Typography variant="overline">ALASAN REQUEST</Typography><Typography>{source?.alasan || detail?.alasan || '-'}</Typography>{(source?.review_notes || detail?.review_notes) && <><Divider /><Typography variant="overline">CATATAN REVIEW</Typography><Typography>{source?.review_notes || detail?.review_notes}</Typography></>}</Card></Grid>
            <Grid item xs={12} md={5}><Card variant="outlined" className="request-audit"><AuditRow label="Requester" actor={source?.requester ?? detail?.requester ?? source?.requester_name} date={source?.requested_at ?? source?.created_at ?? detail?.requested_at} /><AuditRow label="Reviewer" actor={source?.reviewer ?? detail?.reviewer ?? source?.reviewer_name} date={source?.reviewed_at ?? detail?.reviewed_at} /><AuditRow label="Applier" actor={source?.applier ?? detail?.applier ?? source?.applier_name} date={source?.applied_at ?? detail?.applied_at} /></Card></Grid>
          </Grid>

          {hasPlanImpact && <PlanImpactPanel impact={planImpact} />}
        </Stack>}
      </DialogContent>
      {detail && !detailLoading && <DialogActions className="detail-actions">
        <Button color="inherit" onClick={() => setDetailOpen(false)}>Tutup</Button>
        <Box flex={1} />
        {isAllowed(allowedActions, 'cancel') && <Button color="error" variant="outlined" disabled={Boolean(actionLoading)} onClick={cancelRequest}>Batalkan request</Button>}
        {isAllowed(allowedActions, 'edit') && <Button variant="outlined" disabled={Boolean(actionLoading)} onClick={openEdit} startIcon={<Icon icon="mdi:pencil-outline" />}>Edit</Button>}
        {isAllowed(allowedActions, 'reject') && <Button color="error" variant="outlined" disabled={Boolean(actionLoading) || isStale} onClick={() => { setReviewNotes(''); setReviewAction('reject'); }}>Reject</Button>}
        {isAllowed(allowedActions, 'approve') && <Button color="success" variant="contained" disabled={Boolean(actionLoading) || isStale} onClick={() => { setReviewNotes(''); setReviewAction('approve'); }}>Approve</Button>}
        {isAllowed(allowedActions, 'apply') && <Tooltip title={isStale ? 'Request sudah stale dan harus diedit ulang.' : ''}><span><Button variant="contained" disabled={isApplyActionDisabled(isStale, actionLoading)} onClick={beginApply} startIcon={actionLoading === 'apply' ? <CircularProgress size={18} color="inherit" /> : <Icon icon="mdi:check-decagram-outline" />}>{actionLoading === 'apply' ? 'Menerapkan...' : 'Terapkan Perubahan'}</Button></span></Tooltip>}
      </DialogActions>}
    </Dialog>

    <Dialog open={Boolean(reviewAction)} onClose={() => !actionLoading && setReviewAction(null)} fullWidth maxWidth="sm">
      <form onSubmit={submitReview}><DialogTitle>{reviewAction === 'approve' ? 'Approve Request' : 'Reject Request'}<Typography variant="body2" color="text.secondary">Approve belum mengubah Work Order.</Typography></DialogTitle><DialogContent dividers><TextField autoFocus fullWidth multiline minRows={3} required={reviewAction === 'reject'} label="Catatan review" value={reviewNotes} onChange={event => setReviewNotes(event.target.value)} helperText={reviewAction === 'reject' ? 'Alasan penolakan wajib diisi.' : 'Catatan persetujuan bersifat opsional.'} /></DialogContent><DialogActions><Button onClick={() => setReviewAction(null)} disabled={Boolean(actionLoading)}>Batal</Button><Button type="submit" variant="contained" color={reviewAction === 'reject' ? 'error' : 'success'} disabled={Boolean(actionLoading) || (reviewAction === 'reject' && !reviewNotes.trim())}>{actionLoading ? 'Memproses...' : reviewAction === 'approve' ? 'Approve Request' : 'Reject Request'}</Button></DialogActions></form>
    </Dialog>

    <Dialog open={confirmOpen} onClose={() => !actionLoading && setConfirmOpen(false)} fullWidth maxWidth="lg" PaperProps={{ className: 'regeneration-dialog' }}>
      <DialogTitle><Typography variant="h5" fontWeight={850}>Konfirmasi Perubahan Proses dan WO Plan</Typography><Typography variant="body2" color="text.secondary">Step lama yang sudah memiliki aktivitas akan tetap dipertahankan. Step lama yang belum memiliki aktivitas akan dihapus, lalu step proses baru akan ditambahkan setelahnya.</Typography></DialogTitle>
      <DialogContent dividers><Stack spacing={2}><PlanImpactPanel impact={confirmationImpact || {}} compact /><FormControlLabel className="regeneration-control" control={<Checkbox checked={regenerationConfirmed} onChange={event => setRegenerationConfirmed(event.target.checked)} />} label="Saya memahami step yang sudah berjalan akan dipertahankan, step yang belum berjalan akan dihapus, dan step proses baru akan ditambahkan." /></Stack></DialogContent>
      <DialogActions><Button onClick={() => setConfirmOpen(false)} disabled={Boolean(actionLoading)}>Batal</Button><Button variant="contained" color="warning" onClick={() => applyChange(true)} disabled={isConfirmationApplyDisabled(regenerationConfirmed, actionLoading)} startIcon={actionLoading === 'apply' ? <CircularProgress size={18} color="inherit" /> : <Icon icon="mdi:sync-alert" />}>{actionLoading === 'apply' ? 'Menerapkan...' : 'Konfirmasi & Terapkan Perubahan'}</Button></DialogActions>
    </Dialog>
  </div>;
}

export function PlanImpactPanel({ impact, compact = false }: { impact: any; compact?: boolean }) {
  const { preservedSteps, removedSteps, newSteps, finalSteps } = readPlanImpact(impact);
  const message = impact?.confirmation_message || 'Step lama yang sudah memiliki aktivitas akan tetap dipertahankan. Step lama yang belum memiliki aktivitas akan dihapus, lalu step proses baru akan ditambahkan setelahnya.';
  return <Card variant="outlined" className={`plan-impact ${compact ? 'compact' : ''}`}>
    <Box className="section-heading"><Box><Typography variant="h6" fontWeight={800}>Dampak Perubahan Proses</Typography><Typography variant="body2" color="text.secondary">{message}</Typography></Box><Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap><Chip size="small" color="success" label={`${impact?.preserved_plan_count ?? preservedSteps.length} dipertahankan`} /><Chip size="small" color="error" variant="outlined" label={`${impact?.removed_plan_count ?? removedSteps.length} dihapus`} /><Chip size="small" color="primary" label={`${impact?.new_plan_count ?? newSteps.length} step baru`} /><Chip size="small" label={`${impact?.final_plan_count ?? finalSteps.length} hasil akhir`} /></Stack></Box>
    <Grid container spacing={0} className="plan-change-columns">
      <Grid item xs={12} md={4}><PlanStepList title="Step yang Dipertahankan" steps={preservedSteps} tone="preserved" description="Sudah memiliki aktivitas" /></Grid>
      <Grid item xs={12} md={4}><PlanStepList title="Step yang Dihapus" steps={removedSteps} tone="removed" description="Belum memiliki aktivitas" /></Grid>
      <Grid item xs={12} md={4}><PlanStepList title="Step Baru" steps={newSteps} tone="new" description="Akan ditambahkan" /></Grid>
    </Grid>
    <Box className="final-plan-preview"><Box className="final-plan-heading"><Box><Typography fontWeight={850}>Urutan WO Plan Setelah Perubahan</Typography><Typography variant="body2" color="text.secondary">Urutan berikut ditampilkan langsung dari hasil perhitungan backend.</Typography></Box><Chip size="small" label={`${impact?.final_plan_count ?? finalSteps.length} step`} /></Box><PlanStepList title="" steps={finalSteps} tone="final" useArrayOrder /></Box>
  </Card>;
}

function PlanStepList({ title, steps, tone, description, useArrayOrder = false }: { title: string; steps: any[]; tone: 'preserved' | 'removed' | 'new' | 'final'; description?: string; useArrayOrder?: boolean }) {
  return <Box className={`plan-step-list ${tone}`}>{title && <Typography fontWeight={800}>{title}</Typography>}{description && <Typography variant="caption" color="text.secondary" className="plan-list-description">{description}</Typography>}{steps.length ? <Stack spacing={1}>{steps.map((step, index) => <Box className="plan-step" key={step.id || step.wo_plan_id || `${tone}-${index}`}><span>{useArrayOrder ? index + 1 : step.urutan ?? step.no_urut ?? index + 1}</span><Box minWidth={0}><Typography variant="body2" fontWeight={750}>{step.nama_step || step.nama_proses || step.nama || step.name || '-'}</Typography>{description && <Typography variant="caption" className="step-description">{description}</Typography>}</Box></Box>)}</Stack> : <Typography variant="body2" color="text.secondary" className="no-plan">Tidak ada step.</Typography>}</Box>;
}

function AuditRow({ label, actor, date }: { label: string; actor: any; date?: string | null }) {
  return <Box className="audit-row"><Box className="audit-icon"><Icon icon="mdi:account-outline" /></Box><Box><Typography variant="caption">{label}</Typography><Typography variant="body2" fontWeight={750}>{actorLabel(actor)}</Typography><Typography variant="caption" color="text.secondary">{formatDateTime(date)}</Typography></Box></Box>;
}

function StatePanel({ icon, title, text, action }: { icon: string; title: string; text: string; action?: React.ReactNode }) {
  return <Box className="request-state"><Icon icon={icon} /><Typography variant="h6" fontWeight={800}>{title}</Typography><Typography color="text.secondary">{text}</Typography>{action}</Box>;
}

function ListLoading() {
  return <Box className="request-list-loading"><Box className="loading-line header"><Skeleton width={210} /><Skeleton width={130} /></Box>{Array.from({ length: 7 }).map((_, index) => <Box className="loading-line" key={index}><Skeleton width="75%" /><Skeleton width="60%" /><Skeleton width="68%" /><Skeleton width={90} /></Box>)}</Box>;
}

function DetailLoading() {
  return <Stack spacing={2}><Skeleton variant="rounded" height={72} /><Skeleton variant="rounded" height={280} /><Grid container spacing={2}><Grid item xs={12} md={7}><Skeleton variant="rounded" height={150} /></Grid><Grid item xs={12} md={5}><Skeleton variant="rounded" height={150} /></Grid></Grid></Stack>;
}
